import { registerSW } from "virtual:pwa-register";

// Installed PWAs rarely navigate; the browser's default SW re-check can take up to 24h.
const SW_UPDATE_INTERVAL_MS = 60 * 1000;
const CHUNK_RELOAD_KEY = "chunk-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 30 * 1000;

// Installed PWAs rarely navigate, so the browser alone almost never re-checks sw.js.
function watchForSWUpdates(registration) {
  const check = async () => {
    if (registration.installing || !navigator.onLine) return;
    try {
      await registration.update();
    } catch {
      // server unreachable; retry on next tick
    }
  };

  setInterval(check, SW_UPDATE_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.addEventListener("online", check);
  window.addEventListener("focus", check);
  // iOS standalone / bfcache restores
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) check();
  });
}

// After a deploy the old hashed chunks are gone from the server and the SW cache,
// so a lazy route import from an old page fails. This also covers browsers without SW support.
function recoverFromStaleChunks() {
  window.addEventListener("vite:preloadError", (event) => {
    let lastReload = 0;
    try {
      lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    } catch {
      // storage unavailable (private mode); fall through and reload
    }
    if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return;

    event.preventDefault();
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    window.location.reload();
  });
}

/**
 * Registers the service worker in autoUpdate mode (reload on new SW activate)
 * and polls for updates so long-lived mobile PWAs pick up deploys.
 */
export function setupAutoUpdate() {
  recoverFromStaleChunks();
  if (!("serviceWorker" in navigator)) return;

  // Safety net: if a new SW claims this page (skipWaiting/clientsClaim), reload
  // so we never keep running old JS under a new controller.
  // Only when already controlled — otherwise the first install would bounce the page.
  if (navigator.serviceWorker.controller) {
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  }

  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (registration) watchForSWUpdates(registration);
    },
    onOfflineReady() {},
  });
}
